/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ApiResponse } from '../../types/apiTypes';
import {
  InvitationConfig,
  VisitorInvitationPayload,
  InvitationDocument,
  InvitationCustomField,
} from '../../types/visitorInvitationTypes';

export interface VisitorInvitationState {
  invitationConfig: InvitationConfig | null;
  invitationCustomFields: InvitationCustomField[];
  invitationDocuments: ApiResponse<InvitationDocument>;
  invitationSuccess: boolean;
  loadingInvitationConfig: boolean;
  loadingInvitationCustomFields: boolean;
  loadingCreateInvitation: boolean;
  loadingUpdateInvitationConfig: boolean;
  loadingInvitationDocuments: boolean;
  error: string | null;
}

const initialState: VisitorInvitationState = {
  invitationConfig: null,
  invitationCustomFields: [],
  invitationDocuments: {
    data: [],
    meta: null,
    totalCount: 0,
    filteredCount: 0,
  },
  invitationSuccess: false,
  loadingInvitationConfig: false,
  loadingInvitationCustomFields: false,
  loadingCreateInvitation: false,
  loadingUpdateInvitationConfig: false,
  loadingInvitationDocuments: false,
  error: null,
};

type VisitorInvitationLoadingKey =
  | 'loadingInvitationConfig'
  | 'loadingInvitationCustomFields'
  | 'loadingCreateInvitation'
  | 'loadingUpdateInvitationConfig'
  | 'loadingInvitationDocuments';

const visitorInvitationSlice = createSlice({
  name: 'visitorInvitation',
  initialState,
  reducers: {
    createVisitorInvitation(
      state,
      _action: PayloadAction<{
        orgId: number;
        payload: VisitorInvitationPayload;
      }>,
    ) {
      state.error = null;
      state.invitationSuccess = false;
    },
    fetchInvitationConfig(state, _action: PayloadAction<{ orgId: number }>) {
      state.error = null;
    },
    updateInvitationConfig(
      state,
      _action: PayloadAction<{
        orgId: number;
        config: Partial<InvitationConfig>;
      }>,
    ) {
      state.error = null;
    },
    fetchInvitationDocuments(state, _action: PayloadAction<{ orgId: number }>) {
      state.error = null;
    },
    updateInvitationDocuments(
      state,
      _action: PayloadAction<{ orgId: number; documentIds?: number[] }>,
    ) {
      state.error = null;
    },
    fetchInvitationCustomFields(
      state,
      _action: PayloadAction<{ orgId: number }>,
    ) {
      state.error = null;
    },
    createInvitationCustomFields(
      state,
      _action: PayloadAction<{
        orgId: number;
        fieldId: number;
        required: number;
      }>,
    ) {
      state.error = null;
    },
    updateInvitationCustomFields(
      state,
      _action: PayloadAction<{
        orgId: number;
        fieldId: number;
        required: number;
      }>,
    ) {
      state.error = null;
    },
    deleteInvitationCustomFields(
      state,
      _action: PayloadAction<{ orgId: number; fieldId: number }>,
    ) {
      state.error = null;
    },

    setInvitationConfig(state, action: PayloadAction<InvitationConfig>) {
      state.invitationConfig = action.payload;
    },
    setInvitationCustomFields(
      state,
      action: PayloadAction<InvitationCustomField[]>,
    ) {
      state.invitationCustomFields = action.payload;
    },
    setInvitationDocuments(
      state,
      action: PayloadAction<ApiResponse<InvitationDocument>>,
    ) {
      state.invitationDocuments = action.payload;
    },
    setInvitationSuccess(state, action: PayloadAction<boolean>) {
      state.invitationSuccess = action.payload;
    },

    setLoading(
      state,
      action: PayloadAction<{
        key: VisitorInvitationLoadingKey;
        value: boolean;
      }>,
    ) {
      state[action.payload.key] = action.payload.value;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    resetState(state) {
      state.loadingInvitationConfig = false;
      state.loadingCreateInvitation = false;
      state.loadingUpdateInvitationConfig = false;
      state.loadingInvitationDocuments = false;
      state.error = null;
      state.invitationSuccess = false;
    },
    clearVisitorInvitationState() {
      return initialState;
    },
  },
});

export const {
  createVisitorInvitation,
  fetchInvitationConfig,
  updateInvitationConfig,
  fetchInvitationDocuments,
  updateInvitationDocuments,
  fetchInvitationCustomFields,
  createInvitationCustomFields,
  updateInvitationCustomFields,
  deleteInvitationCustomFields,
  setInvitationConfig,
  setInvitationCustomFields,
  setInvitationDocuments,
  setInvitationSuccess,
  setLoading,
  setError,
  resetState,
  clearVisitorInvitationState,
} = visitorInvitationSlice.actions;

export const visitorInvitationActions = visitorInvitationSlice.actions;
export default visitorInvitationSlice.reducer;
